import { useState } from 'react';
import type { Education } from '../../models/v2/profile';
import GlassCard from '../ui/v2/GlassCard';
import { RiGraduationCapLine, RiAddLine, RiPencilLine, RiDeleteBinLine } from 'react-icons/ri';
import ProfileSidePanel from './ProfileSidePanel';
import { profileService } from '../../services/profileService';
import { useProfileMutation } from '../../context/ProfileContext';
import { useConfirm } from '../../context/ConfirmContext';

interface Props {
    educations: Education[];
    onUpdate?: () => void;
}

interface FormData {
    id?: string;
    institution: string;
    degree: string;
    field_of_study: string;
    start_month: string;
    start_year: string;
    end_month: string;
    end_year: string;
    grade: string;
}

const INITIAL_FORM: FormData = {
    institution: '',
    degree: '',
    field_of_study: '',
    start_month: '',
    start_year: '',
    end_month: '',
    end_year: '',
    grade: ''
};

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

export default function EducationSection({ educations, onUpdate }: Props) {
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const { mutate, isMutating } = useProfileMutation();

    const openEdit = (edu: Education) => {
        const startDate = new Date(edu.start_date);
        const endDate = edu.end_date ? new Date(edu.end_date) : null;

        setFormData({
            id: edu.id,
            institution: edu.institution,
            degree: edu.degree,
            field_of_study: edu.field_of_study || '',
            start_month: MONTHS[startDate.getMonth()],
            start_year: startDate.getFullYear().toString(),
            end_month: endDate ? MONTHS[endDate.getMonth()] : '',
            end_year: endDate ? endDate.getFullYear().toString() : '',
            grade: edu.grade || ''
        });
        setEditingId(edu.id);
        setErrors({});
        setIsPanelOpen(true);
    };

    const openAdd = () => {
        setFormData(INITIAL_FORM);
        setEditingId(null);
        setErrors({});
        setIsPanelOpen(true);
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!formData.institution.trim()) newErrors.institution = 'Institution is required';
        if (!formData.degree.trim()) newErrors.degree = 'Degree is required';
        if (!formData.start_month || !formData.start_year) newErrors.start_date = 'Start date is required';
        if (!formData.end_month || !formData.end_year) {
            newErrors.end_date = 'End date (graduated or expected) is required';
        } else {
            const startMonthIdx = MONTHS.indexOf(formData.start_month);
            const endMonthIdx = MONTHS.indexOf(formData.end_month);
            const startDate = new Date(parseInt(formData.start_year), startMonthIdx);
            const endDate = new Date(parseInt(formData.end_year), endMonthIdx);

            if (startDate > endDate) {
                newErrors.end_date = 'End date cannot be before start date';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;

        const startMonthIdx = MONTHS.indexOf(formData.start_month);
        const startDate = new Date(parseInt(formData.start_year), startMonthIdx, 1).toISOString();

        const endMonthIdx = MONTHS.indexOf(formData.end_month);
        const endDate = new Date(parseInt(formData.end_year), endMonthIdx, 1).toISOString();

        const payload = {
            institution: formData.institution,
            degree: formData.degree,
            field_of_study: formData.field_of_study,
            start_date: startDate,
            end_date: endDate,
            grade: formData.grade
        } as unknown as Education;

        if (editingId) {
            mutate(payload, {
                mutationFn: (data: any) => profileService.updateEducation(editingId, data),
                onSuccess: () => {
                    setIsPanelOpen(false);
                    if (onUpdate) onUpdate();
                },
                successMessage: 'Education updated successfully'
            });
        } else {
            mutate(payload, {
                mutationFn: (data: any) => profileService.addEducation(data),
                onSuccess: () => {
                    setIsPanelOpen(false);
                    if (onUpdate) onUpdate();
                },
                successMessage: 'Education added successfully'
            });
        }
    };

    const { ask } = useConfirm();

    const handleDelete = async (id: string) => {
        const confirmed = await ask({
            title: "Delete Education",
            message: "Are you sure you want to delete this education entry? This action cannot be undone.",
            confirmText: "Delete",
            variant: "danger"
        });

        if (confirmed) {
            mutate(null, {
                mutationFn: () => profileService.deleteEducation(id),
                onSuccess: () => {
                    if (onUpdate) onUpdate();
                },
                successMessage: 'Education deleted'
            });
        }
    };

    return (
        <GlassCard>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-white">Education</h2>
                <button
                    onClick={openAdd}
                    className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                >
                    <RiAddLine size={20} />
                </button>
            </div>

            <div className="space-y-4">
                {educations.length === 0 ? (
                    <p className="text-gray-500 text-sm">No education details added.</p>
                ) : (
                    educations.map((edu) => (
                        <div key={edu.id} className="p-4 rounded-xl bg-black/20 border border-white/5 group hover:border-white/10 transition-colors">
                            <div className="flex justify-between items-start">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 shrink-0">
                                        <RiGraduationCapLine className="text-xl" />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-semibold text-white">{edu.institution}</h3>
                                        <p className="text-sm text-gray-400 mt-0.5">{edu.degree} {edu.field_of_study && `• ${edu.field_of_study}`}</p>
                                        <div className="flex items-center mt-2 text-xs text-gray-500 gap-3">
                                            <span>
                                                {new Date(edu.start_date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
                                                {' - '}
                                                {edu.end_date ? new Date(edu.end_date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : 'Present'}
                                            </span>
                                            {edu.grade && <span>• Grade: {edu.grade}</span>}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => openEdit(edu)} className="p-1.5 text-gray-500 hover:text-white transition-colors"><RiPencilLine /></button>
                                    <button onClick={() => handleDelete(edu.id)} className="p-1.5 text-gray-500 hover:text-red-400 transition-colors"><RiDeleteBinLine /></button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <ProfileSidePanel
                isOpen={isPanelOpen}
                onClose={() => setIsPanelOpen(false)}
                title={editingId ? 'Edit Education' : 'Add Education'}
                onSave={handleSave}
                isSaving={isMutating}
            >
                {/* Institution */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">Institution <span className="text-red-400">*</span></label>
                    <input
                        type="text"
                        placeholder="e.g. Stanford University"
                        className={`w-full px-4 py-2.5 rounded-lg bg-black/20 border ${errors.institution ? 'border-red-500/50' : 'border-white/10'} focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 text-white placeholder-gray-600 outline-none transition-all`}
                        value={formData.institution}
                        onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                    />
                    {errors.institution && <p className="text-xs text-red-400">{errors.institution}</p>}
                </div>

                {/* Degree */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">Degree <span className="text-red-400">*</span></label>
                    <input
                        type="text"
                        placeholder="e.g. Bachelor's, Master's"
                        className={`w-full px-4 py-2.5 rounded-lg bg-black/20 border ${errors.degree ? 'border-red-500/50' : 'border-white/10'} focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 text-white placeholder-gray-600 outline-none transition-all`}
                        value={formData.degree}
                        onChange={(e) => setFormData({ ...formData, degree: e.target.value })}
                    />
                    {errors.degree && <p className="text-xs text-red-400">{errors.degree}</p>}
                </div>

                {/* Field of Study */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">Field of Study</label>
                    <input
                        type="text"
                        placeholder="e.g. Computer Science"
                        className="w-full px-4 py-2.5 rounded-lg bg-black/20 border border-white/10 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 text-white placeholder-gray-600 outline-none transition-all"
                        value={formData.field_of_study}
                        onChange={(e) => setFormData({ ...formData, field_of_study: e.target.value })}
                    />
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 sm:col-span-1 space-y-2">
                        <label className="text-sm font-medium text-gray-300">Start Date <span className="text-red-400">*</span></label>
                        <div className="grid grid-cols-2 gap-2">
                            <select
                                className={`w-full px-2 py-2.5 rounded-lg bg-black/20 border ${errors.start_date ? 'border-red-500/50' : 'border-white/10'} text-white outline-none appearance-none`}
                                value={formData.start_month}
                                onChange={(e) => setFormData({ ...formData, start_month: e.target.value })}
                            >
                                <option value="" className="bg-[#0c0e14]">Month</option>
                                {MONTHS.map(m => <option key={m} value={m} className="bg-[#0c0e14]">{m}</option>)}
                            </select>
                            <input
                                type="number"
                                placeholder="Year"
                                className={`w-full px-3 py-2.5 rounded-lg bg-black/20 border ${errors.start_date ? 'border-red-500/50' : 'border-white/10'} text-white placeholder-gray-600 outline-none`}
                                value={formData.start_year}
                                onChange={(e) => setFormData({ ...formData, start_year: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="col-span-2 sm:col-span-1 space-y-2">
                        <label className="text-sm font-medium text-gray-300">End Date (Expected/Actual) <span className="text-red-400">*</span></label>
                        <div className="grid grid-cols-2 gap-2">
                            <select
                                className={`w-full px-2 py-2.5 rounded-lg bg-black/20 border ${errors.end_date ? 'border-red-500/50' : 'border-white/10'} text-white outline-none appearance-none`}
                                value={formData.end_month}
                                onChange={(e) => setFormData({ ...formData, end_month: e.target.value })}
                            >
                                <option value="" className="bg-[#0c0e14]">Month</option>
                                {MONTHS.map(m => <option key={m} value={m} className="bg-[#0c0e14]">{m}</option>)}
                            </select>
                            <input
                                type="number"
                                placeholder="Year"
                                className={`w-full px-3 py-2.5 rounded-lg bg-black/20 border ${errors.end_date ? 'border-red-500/50' : 'border-white/10'} text-white placeholder-gray-600 outline-none`}
                                value={formData.end_year}
                                onChange={(e) => setFormData({ ...formData, end_year: e.target.value })}
                            />
                        </div>
                        {errors.end_date && <p className="text-xs text-red-400 mt-1">{errors.end_date}</p>}
                    </div>
                </div>

                {/* Grade */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">Grade / GPA</label>
                    <input
                        type="text"
                        placeholder="e.g. 4.0, First Class Honours"
                        className="w-full px-4 py-2.5 rounded-lg bg-black/20 border border-white/10 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 text-white placeholder-gray-600 outline-none transition-all"
                        value={formData.grade}
                        onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                    />
                </div>

            </ProfileSidePanel>
        </GlassCard>
    );
}
